import axios from '../axiosConfig';
import { Comment, CommentsResponse, CreateCommentPayload, CommentQueryParams } from '../types/comment';
import { downloadFile as downloadStoredFile, uploadFile } from './fileService';

/**
 * Fetch comments with optional filters using GET endpoint with query parameters
 */
export async function fetchComments(params?: CommentQueryParams): Promise<CommentsResponse> {
    const query: Record<string, any> = {};

    if (params?.EntityId) query.EntityId = params.EntityId;
    if (params?.UserId) query.UserId = params.UserId;
    if (params?.Block) query.Block = params.Block;
    if (params?.Section) query.Section = params.Section;
    if (params?.FromDate) query.FromDate = params.FromDate;
    if (params?.ToDate) query.ToDate = params.ToDate;
    if (params?.TextSearch) query.TextSearch = params.TextSearch;
    if (params?.Page != null) query.Page = params.Page;
    if (params?.PageSize != null) query.PageSize = params.PageSize;
    if (params?.Skip != null) query.Skip = params.Skip;
    if (params?.IncludeDeleted != null) query.IncludeDeleted = params.IncludeDeleted;
    if (params?.OrderByField) query.OrderByField = params.OrderByField;
    if (params?.Descending != null) query.Descending = params.Descending;

    const { data } = await axios.get<CommentsResponse>('/Comments', { params: query });
    return data;
}

/**
 * Create a new comment
 */
export async function createComment(payload: Partial<CreateCommentPayload>): Promise<Comment> {
    const { data } = await axios.post<Comment>('/Comments', payload);
    return data;
}

/**
 * Update an existing comment
 */
export async function updateComment(id: string, payload: Partial<CreateCommentPayload>): Promise<Comment> {
    const { data } = await axios.put<Comment>(`/Comments/${encodeURIComponent(id)}`, payload);
    return data;
}

/**
 * Delete a comment
 */
export async function deleteComment(id: string): Promise<void> {
    await axios.delete(`/Comments/${encodeURIComponent(id)}`);
}

/**
 * Upload a file attachment for comments
 */
export async function uploadCommentAttachment(file: File): Promise<string> {
    return uploadFile(file);
}

/**
 * Download a file attachment
 */
export async function downloadFile(filename: string): Promise<Blob> {
    return downloadStoredFile(filename);
}

export interface UserOption {
    id: string;
    userId?: string;
    name: string;
    emailAddress: string;
    avatarUri?: string | null;
}

/**
 * Fetch all users for mentions
 */
export async function fetchUsersForMentions(): Promise<UserOption[]> {
    const { data } = await axios.get('/Users');
    const usersList: UserOption[] = Array.isArray((data as any)?.results)
        ? ((data as any).results as Array<{ id?: string; userId?: string; name?: string; emailAddress?: string; avatarUri?: string | null }>)
            .map(u => ({
                id: String(u?.id || '').trim(),
                userId: u?.userId ? String(u.userId) : undefined,
                name: (u?.name || '').trim(),
                emailAddress: (u?.emailAddress || '').trim(),
                avatarUri: u?.avatarUri ?? null,
            }))
            .filter(u => Boolean(u.id) && Boolean(u.name) && Boolean(u.emailAddress))
        : [];
    return usersList;
}
